import sanitizeHtml from 'sanitize-html';

import type { UniformPricingTiersTableProps } from './pricing-tiers-table.tsx';
import type { Document } from '@contentful/rich-text-types';

export const pricingTiersTableContentType = 'pricingTableRef';

export interface ParsePricingTiersTableEmbeddedEntryArgs {
	fields: unknown;
	renderRichText: (document: Document) => string;
}

export function parsePricingTiersTableEmbeddedEntry(
	args: ParsePricingTiersTableEmbeddedEntryArgs,
): string | undefined {
	const props = getPricingTiersTableProps(args);
	return props
		? `<div data-pricing-tiers-table="${encodeURIComponent(JSON.stringify(props))}"></div>`
		: undefined;
}

export interface GetPricingTiersTablePropsArgs {
	fields: unknown;
	renderRichText: (document: Document) => string;
}

export function getPricingTiersTableProps(
	args: GetPricingTiersTablePropsArgs,
): UniformPricingTiersTableProps | undefined {
	const { fields, renderRichText } = args;
	const pricingTableFields = fields as
		| (Partial<
				Pick<UniformPricingTiersTableProps, 'sku' | 'title' | 'titleHref' | 'billingCycle' | 'unit'>
		  > & { manualTableOverride?: Document; tableHtml?: string })
		| undefined;
	if (!pricingTableFields) {
		return undefined;
	}

	const { sku, title, titleHref, billingCycle, unit, manualTableOverride, tableHtml } =
		pricingTableFields;
	let authoredTableHtml = sanitizePricingTableHtml(tableHtml || '');
	if (
		!/<table(?:\s|>)/i.test(authoredTableHtml) &&
		manualTableOverride?.nodeType === 'document' &&
		manualTableOverride.content?.some((node) => node.nodeType === 'table')
	) {
		authoredTableHtml = sanitizePricingTableHtml(renderRichText(manualTableOverride));
	}
	if (/<table(?:\s|>)/i.test(authoredTableHtml)) {
		return { ...(sku && { sku }), manualTableOverrideHtml: authoredTableHtml };
	}

	if (!sku || !title || !titleHref || !billingCycle) {
		return undefined;
	}

	return { sku, title, titleHref, billingCycle, unit };
}

function sanitizePricingTableHtml(html: string): string {
	return sanitizeHtml(html, {
		allowedTags: [
			'table',
			'caption',
			'thead',
			'tbody',
			'tfoot',
			'tr',
			'th',
			'td',
			'p',
			'h3',
			'h4',
			'ul',
			'ol',
			'li',
			'br',
			'a',
			'strong',
			'b',
			'em',
			'i',
			'u',
			'sup',
			'sub',
			'span',
		],
		allowedAttributes: {
			a: ['href', 'title'],
			th: ['colspan', 'rowspan', 'scope', 'headers', 'id'],
			td: ['colspan', 'rowspan', 'headers', 'id'],
		},
		allowedSchemes: ['http', 'https', 'mailto'],
		allowProtocolRelative: false,
	});
}
